<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { animate, motion } from 'motion-v'
import type { Report } from '@/api/schemas'
import { letterGrade } from '@/api/schemas'
import { submitFollowup, ApiRequestError } from '@/api/client'

const props = defineProps<{ report: Report; auditId: number | null }>()

const displayedScore = ref(0)

const followupEmail = ref('')
const followupStatus = ref<'idle' | 'submitting' | 'success' | 'invalid' | 'error'>('idle')
const followupError = ref('')

async function onFollowupSubmit() {
  if (props.auditId === null) return
  followupStatus.value = 'submitting'
  followupError.value = ''
  try {
    await submitFollowup(props.auditId, followupEmail.value)
    followupStatus.value = 'success'
  } catch (e) {
    if (e instanceof ApiRequestError && e.status === 422) {
      followupStatus.value = 'invalid'
    } else {
      followupStatus.value = 'error'
      followupError.value =
        e instanceof ApiRequestError
          ? e.message
          : 'Something went wrong sending that — try again, or reach me directly below.'
    }
  }
}

// Count-up animation when the report (re)appears.
watch(
  () => props.report,
  (r) => {
    displayedScore.value = 0
    animate(0, r.summary.score, {
      duration: 1.0,
      ease: 'easeOut',
      onUpdate: (v) => {
        displayedScore.value = v
      },
    })
  },
  { immediate: true },
)

const scoreClass = computed(() => {
  const s = props.report.summary.score
  if (s >= 80) return 'score-good'
  if (s >= 60) return 'score-okay'
  return 'score-bad'
})

const formattedTimestamp = computed(() => {
  const d = new Date(props.report.timestamp)
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
})

function detailEntries(details: Record<string, unknown> | undefined) {
  if (!details) return []
  return Object.entries(details)
}

function isList(v: unknown): v is unknown[] {
  return Array.isArray(v)
}

function ctaHref(host: string) {
  const subj = encodeURIComponent(`Help with ${host} audit`)
  return `mailto:dallan.baker@castlecountrydigital.com?subject=${subj}`
}
</script>

<template>
  <motion.article
    class="report"
    :initial="{ opacity: 0, y: 20 }"
    :animate="{ opacity: 1, y: 0 }"
    :transition="{ duration: 0.5, ease: 'easeOut' }"
  >
    <header class="report-header">
      <div>
        <h3>
          Audit for <span class="report-host">{{ report.host }}</span>
        </h3>
        <p class="muted">Completed {{ formattedTimestamp }}</p>
      </div>
      <div :class="['score', scoreClass]">
        <div class="score-num">{{ Math.round(displayedScore) }}</div>
        <div class="score-grade">Grade {{ letterGrade(report.summary.score) }}</div>
      </div>
    </header>

    <div class="report-summary">
      <span class="badge badge-pass">{{ report.summary.passed }} passing</span>
      <span class="badge badge-warn">{{ report.summary.warned }} warnings</span>
      <span class="badge badge-fail">{{ report.summary.failed }} failures</span>
    </div>

    <ol class="checks">
      <motion.li
        v-for="(check, i) in report.checks"
        :key="check.key"
        :class="['check', `check-${check.status}`]"
        :initial="{ opacity: 0, x: -8 }"
        :animate="{ opacity: 1, x: 0 }"
        :transition="{ duration: 0.4, delay: 0.1 + i * 0.06, ease: 'easeOut' }"
      >
        <div class="check-header">
          <span class="check-status">{{ check.status }}</span>
          <h4>{{ check.title }}</h4>
        </div>
        <p class="check-message">{{ check.message }}</p>
        <details v-if="check.details" class="check-details">
          <summary>Technical details</summary>
          <dl>
            <template v-for="[k, v] in detailEntries(check.details)" :key="k">
              <dt>{{ k }}</dt>
              <dd>
                <ul v-if="isList(v) && v.length" class="detail-list">
                  <li v-for="(item, idx) in v" :key="idx">{{ item }}</li>
                </ul>
                <em v-else-if="isList(v)">none</em>
                <template v-else>{{ v }}</template>
              </dd>
            </template>
          </dl>
        </details>
      </motion.li>
    </ol>

    <footer class="report-cta">
      <h3>Want help fixing this?</h3>

      <template v-if="auditId !== null">
        <p>
          I work with businesses across Carbon and Emery County on exactly this. The list above is
          what a scanner can see. If you want me to go through your security setup myself and write
          back with what I'd fix first and what it'd cost, leave your email.
        </p>

        <form
          v-if="followupStatus !== 'success'"
          class="followup-form"
          @submit.prevent="onFollowupSubmit"
        >
          <div class="field">
            <label for="followup-email">Your email</label>
            <input
              id="followup-email"
              v-model="followupEmail"
              type="email"
              name="email"
              placeholder="you@example.com"
              autocomplete="email"
              required
              :disabled="followupStatus === 'submitting'"
            />
          </div>
          <button type="submit" class="btn btn-primary" :disabled="followupStatus === 'submitting'">
            {{ followupStatus === 'submitting' ? 'Sending…' : 'Get my take' }}
          </button>
        </form>

        <p v-if="followupStatus === 'invalid'" class="followup-error" role="alert">
          That doesn't look like a valid email address.
        </p>
        <p v-else-if="followupStatus === 'error'" class="followup-error" role="alert">
          {{ followupError }}
        </p>
        <p v-else-if="followupStatus === 'success'" class="followup-success">
          Got it — I'll go through this myself and write back.
        </p>

        <p class="report-cta-alt">
          Or call (385) 301-4032, or
          <a :href="ctaHref(report.host)">email me directly</a>.
        </p>
      </template>

      <template v-else>
        <p>
          I work with businesses across Carbon and Emery County on exactly this. Email me about
          what's above and I'll tell you what I'd fix first and what it would cost, or call (385)
          301-4032.
        </p>
        <a class="btn btn-primary" :href="ctaHref(report.host)"> Email me about this report </a>
      </template>
    </footer>
  </motion.article>
</template>

<style scoped>
.report {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);
}

.report-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.5rem;
  border-bottom: 1px solid var(--border);
  padding-bottom: 1rem;
}

.report-header h3 {
  margin: 0;
  font-size: 1.2rem;
}
.report-host {
  color: var(--accent-bright);
  font-weight: 700;
}
.muted {
  color: var(--text-muted);
}

.score {
  text-align: center;
  padding: 0.5rem 1.25rem;
  border-radius: var(--radius);
  min-width: 90px;
}
.score-num {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}
.score-grade {
  font-size: 0.85rem;
  color: var(--text-muted);
  margin-top: 0.25rem;
}
.score-good {
  background: var(--pass-tint);
  color: var(--pass);
  border: 1px solid var(--pass);
}
.score-okay {
  background: var(--warn-tint);
  color: var(--warn);
  border: 1px solid var(--warn);
}
.score-bad {
  background: var(--fail-tint);
  color: var(--fail);
  border: 1px solid var(--fail);
}

.report-summary {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin: 1rem 0;
}

.badge {
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
}
.badge-pass {
  background: var(--pass-tint);
  color: var(--pass);
  border: 1px solid var(--pass);
}
.badge-warn {
  background: var(--warn-tint);
  color: var(--warn);
  border: 1px solid var(--warn);
}
.badge-fail {
  background: var(--fail-tint);
  color: var(--fail);
  border: 1px solid var(--fail);
}

.checks {
  list-style: none;
  padding: 0;
  margin: 1.5rem 0;
}

.check {
  border-left: 3px solid var(--border);
  background: var(--surface-2);
  padding: 1rem 1.25rem;
  margin-bottom: 0.75rem;
  border-radius: 0 var(--radius) var(--radius) 0;
}
.check-pass {
  border-left-color: var(--pass);
}
.check-warn {
  border-left-color: var(--warn);
}
.check-fail {
  border-left-color: var(--fail);
}

.check-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.check-header h4 {
  margin: 0;
  font-size: 1rem;
}

.check-status {
  text-transform: uppercase;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  background: var(--bg);
  color: var(--text-muted);
}
.check-pass .check-status {
  background: var(--pass-tint);
  color: var(--pass);
  border: 1px solid var(--pass);
}
.check-warn .check-status {
  background: var(--warn-tint);
  color: var(--warn);
  border: 1px solid var(--warn);
}
.check-fail .check-status {
  background: var(--fail-tint);
  color: var(--fail);
  border: 1px solid var(--fail);
}

.check-message {
  margin: 0.5rem 0 0;
  color: var(--text);
}

.check-details {
  margin-top: 0.75rem;
  font-size: 0.85rem;
}
.check-details summary {
  cursor: pointer;
  color: var(--text-muted);
}
.check-details dl {
  margin: 0.5rem 0 0;
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 0.25rem 0.75rem;
}
.check-details dt {
  color: var(--text-muted);
}
.check-details dd {
  margin: 0;
  word-break: break-word;
}
.detail-list {
  margin: 0;
  padding-left: 1.1rem;
}
.detail-list li {
  margin: 0.15rem 0;
}

.report-cta {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border);
  text-align: center;
}
.report-cta h3 {
  margin: 0 0 0.5rem;
}
.report-cta p {
  color: var(--text-muted);
  max-width: 38em;
  margin: 0 auto 1rem;
}

.followup-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.85rem;
  max-width: 22em;
  margin: 0 auto 1rem;
}
.followup-form .field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  width: 100%;
  text-align: left;
}
.followup-form .field label {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-muted);
}
.followup-form input {
  background: var(--bg);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 0.7rem 0.9rem;
  font-size: 1rem;
  font-family: inherit;
  width: 100%;
}
.followup-form input:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(162, 59, 29, 0.15);
}
.followup-form input:disabled {
  opacity: 0.6;
}

.followup-error {
  color: var(--fail);
  background: var(--fail-tint);
  border: 1px solid var(--fail);
  border-radius: var(--radius);
  padding: 0.6rem 1rem;
  max-width: 30em;
  margin: 0 auto 1rem;
  font-size: 0.9rem;
}
.followup-success {
  color: var(--pass);
  font-weight: 600;
  margin: 0 auto 1rem;
}

.report-cta-alt {
  color: var(--text-muted);
  font-size: 0.9rem;
  margin: 0;
}
.report-cta-alt a {
  color: var(--accent-bright);
}

.btn {
  display: inline-block;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: var(--radius);
  font-size: 1rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  text-align: center;
  text-decoration: none;
}
.btn-primary {
  background: var(--accent);
  color: var(--on-accent);
}
.btn-primary:hover {
  background: var(--accent-bright);
}

@media (max-width: 600px) {
  .report-header {
    flex-direction: column;
  }
  .score {
    align-self: flex-start;
  }
}
</style>
